import { ref, computed } from 'vue'
import { api } from '../services/api'

const LS_KEY = (uid) => `poderi2_prefs_${uid}`

// Stato condiviso tra tutte le istanze del composable
const _prefs = ref({ starred_farms: [], archived_farms: [] })
const _loading = ref(false)
const _uid = ref(null)

function _fromLS(uid) {
  try {
    const raw = localStorage.getItem(LS_KEY(uid))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function _toLS(uid, prefs) {
  try {
    localStorage.setItem(LS_KEY(uid), JSON.stringify(prefs))
  } catch {}
}

async function _load(uid) {
  _uid.value = uid
  // Carica subito dalla cache
  const cached = _fromLS(uid)
  if (cached) _prefs.value = { starred_farms: [], archived_farms: [], ...cached }

  // Poi dal server
  try {
    const res = await api.get('/api/v2/poderi2/profile')
    if (res?.ok && res.people?.json?.farm_preferences) {
      const serverPrefs = res.people.json.farm_preferences
      _prefs.value = { starred_farms: [], archived_farms: [], ...serverPrefs }
      _toLS(uid, _prefs.value)
    }
  } catch {}
}

async function _save() {
  if (!_uid.value) return
  const prefs = { ..._prefs.value }
  _toLS(_uid.value, prefs)

  try {
    // Manda solo il patch — il backend fa merge JSONB, non sovrascrive altri campi
    await api.put('/api/v2/poderi2/profile', { json: { farm_preferences: prefs } })
  } catch {}
}

export function useUserPreferences() {
  const starredFarms = computed(() => new Set(_prefs.value.starred_farms || []))
  const archivedFarms = computed(() => new Set(_prefs.value.archived_farms || []))

  function isStarred(id) { return starredFarms.value.has(id) }
  function isArchived(id) { return archivedFarms.value.has(id) }

  async function toggleStar(id) {
    const list = [...(_prefs.value.starred_farms || [])]
    const idx = list.indexOf(id)
    if (idx === -1) list.push(id)
    else list.splice(idx, 1)
    _prefs.value = { ..._prefs.value, starred_farms: list }
    await _save()
  }

  async function toggleArchive(id) {
    const archived = [...(_prefs.value.archived_farms || [])]
    const starred = [...(_prefs.value.starred_farms || [])]
    const idx = archived.indexOf(id)
    if (idx === -1) {
      archived.push(id)
      // Rimuovi dai preferiti se archiviata
      const si = starred.indexOf(id)
      if (si !== -1) starred.splice(si, 1)
    } else {
      archived.splice(idx, 1)
    }
    _prefs.value = { ..._prefs.value, archived_farms: archived, starred_farms: starred }
    await _save()
  }

  return {
    loadPreferences: _load,
    isStarred,
    isArchived,
    toggleStar,
    toggleArchive,
    loading: _loading,
  }
}
